import { useMemo } from 'react'
import PropTypes from 'prop-types'
import EducationFacilityMap from '../EducationFacilityMap'

/**
 * EducationOverviewChart (Bare) - Integrated overview with stats and map
 * Combines key statistics cards with interactive facility map
 * For embedding in narrative HTML templates
 */
function EducationOverviewChart({ data }) {
  const { metrics, facilities, location } = data

  // Calculate key statistics for display cards
  const keyStats = useMemo(() => {
    if (!metrics) return []

    const ptrValue = metrics.educationRatios?.pupilTeacherRatio || 0
    const gpiValue = metrics.genderParity?.parityIndex || 1.0

    return [
      {
        label: 'Total Schools',
        value: metrics.totalInstitutions?.toLocaleString() || '0',
        icon: '🏫',
        status: metrics.totalInstitutions > 0 ? 'good' : 'poor',
      },
      {
        label: 'Total Learners',
        value: metrics.totalLearners?.toLocaleString() || '0',
        icon: '👨‍🎓',
        status: 'neutral',
      },
      {
        label: 'Pupil-Teacher Ratio',
        value: ptrValue > 0 ? `${Math.round(ptrValue)}:1` : 'N/A',
        icon: '📊',
        status:
          ptrValue > 0 && ptrValue <= 40
            ? 'good'
            : ptrValue > 40
              ? 'poor'
              : 'neutral',
        subtitle:
          ptrValue > 0 && ptrValue <= 40
            ? 'Meets Standard'
            : ptrValue > 40
              ? 'Above Standard'
              : '',
      },
      {
        label: 'Gender Parity Index',
        value: gpiValue.toFixed(2),
        icon: '⚖️',
        status: gpiValue >= 0.97 && gpiValue <= 1.03 ? 'good' : 'moderate',
        subtitle:
          gpiValue >= 0.97 && gpiValue <= 1.03
            ? 'Balanced'
            : gpiValue < 0.97
              ? 'Fewer Girls'
              : 'Fewer Boys',
      },
    ]
  }, [metrics])

  if (!metrics || !facilities || !location) {
    return (
      <div className="text-center py-8 text-gray-500">
        No overview data available
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Key Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {keyStats.map((stat, index) => (
          <div
            key={index}
            className={`rounded-lg p-4 border-2 h-full ${
              stat.status === 'good'
                ? 'bg-emerald-50 border-emerald-300'
                : stat.status === 'poor'
                  ? 'bg-red-50 border-red-300'
                  : stat.status === 'moderate'
                    ? 'bg-yellow-50 border-yellow-300'
                    : 'bg-blue-50 border-blue-300'
            }`}
          >
            <div className="text-2xl mb-1">{stat.icon}</div>
            <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
            <div className="text-xs text-gray-600 mt-1">{stat.label}</div>
            {stat.subtitle && (
              <div className="text-xs text-gray-500 mt-1 italic">
                {stat.subtitle}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Education Facility Map */}
      {facilities.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <EducationFacilityMap
            facilities={facilities}
            location={location}
            title="School Locations"
          />
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          No schools found for this location
        </div>
      )}
    </div>
  )
}

EducationOverviewChart.propTypes = {
  data: PropTypes.shape({
    metrics: PropTypes.shape({
      totalInstitutions: PropTypes.number,
      totalLearners: PropTypes.number,
      totalTeachers: PropTypes.number,
      educationRatios: PropTypes.shape({
        pupilTeacherRatio: PropTypes.number,
      }),
      genderParity: PropTypes.shape({
        parityIndex: PropTypes.number,
      }),
    }).isRequired,
    facilities: PropTypes.array.isRequired,
    location: PropTypes.shape({
      name: PropTypes.string.isRequired,
      code: PropTypes.string,
    }).isRequired,
  }).isRequired,
}

export default EducationOverviewChart
